/**
 * Thin DOM adapter: turns `keydown` KeyboardEvents on a window/element into
 * the pure machine's data events and implements {@link CaptureSource}.
 * All decision logic lives in `wedge.ts`; this file only bridges the DOM.
 */
import type { CaptureErrorHandler, CaptureSource, ScanHandler, ScanResult } from "./types";
import { KeyboardWedgeMachine, type WedgeMachineOptions } from "./wedge";
import { detectSymbology } from "./symbology";

export type EditableMode = "global" | "ignore-editable";

export interface KeyboardWedgeSourceOptions extends WedgeMachineOptions {
  /**
   * Event target to listen on. Default: `window` (resolved lazily at
   * `start()`, so constructing the source in a non-DOM environment is safe).
   */
  target?: EventTarget;
  /**
   * - `"global"`: observe every keydown, even ones typed into
   *   inputs/textareas — floor screens (pick/pack, GRN, counts, RMA
   *   receiving) capture globally so the scanner works no matter what has
   *   focus. The adapter OBSERVES; it never intercepts buffered characters
   *   (they can't be known to belong to a scan until the burst completes),
   *   so a scan aimed at a focused input still types into it — floor screens
   *   should keep focus parked on a scan field. The one event the adapter
   *   may cancel is the completing terminator ({@link suppressTerminator}).
   * - `"ignore-editable"`: skip events whose target is an `input`,
   *   `textarea`, `select`, or contenteditable element — back-office screens
   *   listen window-wide without stealing keystrokes from forms, and pair
   *   this with an ever-available scan box that runs its own element-scoped
   *   source. Caveat: shadow-DOM retargeting exposes the host element for
   *   events from inside a shadow root, so a focused input in a shadow root
   *   does NOT look editable here and its keystrokes are still observed.
   *
   * Default: `"ignore-editable"` when listening on window/document,
   * `"global"` when `target` is a specific element (an element-scoped scan
   * box IS the editable element, so ignoring editables there would ignore
   * everything).
   */
  editableMode?: EditableMode;
  /**
   * When a keydown completes a scan (the terminator of an emitted burst),
   * call `preventDefault()`/`stopPropagation()` on that event so the scan's
   * Enter can't also trigger unrelated default behavior — most importantly a
   * focused `<input>`'s implicit form submission in `"global"` mode. Only
   * the completing terminator is suppressed; ordinary typing (including a
   * terminator that does NOT complete a scan) is untouched. Default `true`.
   */
  suppressTerminator?: boolean;
  /**
   * Clock override. When provided it is used INSTEAD of `event.timeStamp`
   * for every event — inject a controlled clock in tests (jsdom timeStamps
   * are unreliable) or to align with an app-wide clock.
   */
  now?: () => number;
  /** Overrides {@link CaptureSource.id}. Default `"keyboard-wedge"`. */
  sourceId?: string;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (typeof Element === "undefined" || !(target instanceof Element)) {
    return false;
  }
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return target instanceof HTMLElement && target.isContentEditable;
}

/**
 * Keyboard-wedge {@link CaptureSource}. The Phase-4 camera sources
 * (BarcodeDetector API, ZXing-wasm) implement the same interface; consumers
 * never depend on this class directly except to construct it.
 */
export class KeyboardWedgeSource implements CaptureSource {
  readonly id: string;
  private readonly options: KeyboardWedgeSourceOptions;
  private readonly machine: KeyboardWedgeMachine;
  private readonly handlers = new Set<ScanHandler>();
  private readonly errorHandlers = new Set<CaptureErrorHandler>();
  private target: EventTarget | null = null;

  constructor(options: KeyboardWedgeSourceOptions = {}) {
    this.options = options;
    this.id = options.sourceId ?? "keyboard-wedge";
    this.machine = new KeyboardWedgeMachine(options);
  }

  start(): void {
    if (this.target !== null) return; // already started
    const target = this.options.target ?? (typeof window !== "undefined" ? window : null);
    if (target === null) {
      throw new Error(
        "KeyboardWedgeSource.start(): no event target — no `window` in this environment and no `target` option given",
      );
    }
    this.target = target;
    target.addEventListener("keydown", this.onKeyDown);
  }

  stop(): void {
    if (this.target === null) return;
    this.target.removeEventListener("keydown", this.onKeyDown);
    this.target = null;
    this.machine.reset(); // discard any half-captured burst
  }

  attach(handler: ScanHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  /**
   * Contract-completeness only: the wedge has no asynchronous failure mode
   * the DOM can report (a dead/unplugged HID scanner is indistinguishable
   * from silence), so this never fires. Camera sources (Phase 4) do fire it.
   */
  attachError(handler: CaptureErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => {
      this.errorHandlers.delete(handler);
    };
  }

  private resolveEditableMode(): EditableMode {
    if (this.options.editableMode !== undefined) return this.options.editableMode;
    const targetIsElement =
      typeof Element !== "undefined" && this.options.target instanceof Element;
    return targetIsElement ? "global" : "ignore-editable";
  }

  private readonly onKeyDown = (event: Event): void => {
    const keyboardEvent = event as KeyboardEvent;
    const key = keyboardEvent.key;
    if (typeof key !== "string") return; // not a keyboard event
    // OS key auto-repeat can fire faster than the inter-key threshold
    // (~30-33ms at the fastest OS settings vs the 35ms default), so a held
    // key would synthesize a phantom burst. A real wedge never auto-repeats.
    if (keyboardEvent.repeat === true) return;
    if (this.resolveEditableMode() === "ignore-editable" && isEditableTarget(event.target)) {
      return;
    }
    const timeStampMs = this.options.now ? this.options.now() : event.timeStamp;
    const emission = this.machine.feed({ key, timeStampMs });
    if (emission === null) return;
    if (this.options.suppressTerminator !== false) {
      event.preventDefault();
      event.stopPropagation();
    }
    const scan: ScanResult = {
      raw: emission.raw,
      value: emission.value,
      symbology: detectSymbology(emission.value),
      sourceId: this.id,
      timestampMs: emission.timestampMs,
    };
    for (const handler of [...this.handlers]) {
      handler(scan);
    }
  };
}
